import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';

// ---------------------------------------------------------------------------
// PII field names – any request/response JSON key that matches (case-
// insensitive) will be redacted before the data leaves the browser.
// ---------------------------------------------------------------------------
const PII_FIELDS = [
  // Names
  'name', 'firstName', 'first_name', 'lastName', 'last_name', 'fullName',
  'full_name', 'displayName', 'display_name', 'username', 'userName',
  // Contact
  'email', 'emailAddress', 'email_address', 'contactemail', 'phone', 'phoneNumber',
  'phone_number', 'mobile', 'mobileNumber', 'mobile_number', 'fax',
  // Address
  'address', 'address1', 'address2', 'addressLine1', 'addressLine2',
  'address_line_1', 'address_line_2', 'street', 'city', 'state',
  'stateCode', 'state_code', 'zip', 'zipCode', 'zip_code', 'postalCode',
  'postal_code', 'country', 'countryCode', 'country_code',
  // Payment / financial
  'cardNumber', 'card_number', 'ccNumber', 'cc_number', 'cvv', 'cvc',
  'securityCode', 'security_code', 'expirationDate', 'expiration_date',
  'expMonth', 'expYear', 'exp_month', 'exp_year', 'last4', 'last_4',
  'accountNumber',
  'account_number', 'routingNumber', 'routing_number', 'iban', 'bic',
  'pan', 'cardholderName', 'cardholder_name',
  // Auth (including change-password payload keys)
  'password', 'oldPassword', 'newPassword', 'confirmPassword', 'currentPassword',
  'token', 'jwtToken', 'accessToken', 'access_token', 'refreshToken',
  'refresh_token', 'ssn', 'socialSecurityNumber', 'social_security_number',
  'dob', 'dateOfBirth', 'date_of_birth', 'birthDate', 'birth_date', 'reAuthToken',
  // B2B / SFCC identifiers (HAR: extauth login payload)
  'becustno', 'ecomcustno', 'ecomcustid', 'custhqaccount', 'custdefaultsc',
  'companynumber', 'login', 'employeeid', 'userid',
  'customerAccountNumber', 'customeraccountnumber',
];

const piiSet = new Set(PII_FIELDS.map(f => f.toLowerCase()));

function isSensitiveKey(lk) {
  if (piiSet.has(lk)) return true;
  if (lk.includes('password')) return true;
  // SSO / profile (e.g. c_B2B_EmployeeID, EmployeeID)
  if (lk.includes('employeeid')) return true;
  // JWT / opaque tokens in response bodies
  if (lk === 'jwt' || lk.includes('jwt') || (lk.endsWith('token') && lk !== 'csrftoken')) return true;
  return false;
}

// HTTP headers whose values must not be sent to LogRocket (matches api.ts prepareHeaders / server injection).
const SENSITIVE_HEADER_NAMES = new Set([
  'authorization', 'cookie', 'set-cookie', 'x-api-key', 'token', 'refreshtoken',
  'becustno', 'ecomcustno', 'sitehqaccount', 'sitedefaultsc', 'companynumber',
  'userid', 'consumerlocation', 'login', 'employeeid',
]);

function sanitizeHeaders(headers) {
  if (!headers) return;
  const lower = Object.keys(headers).reduce((acc, k) => {
    acc[k.toLowerCase()] = k;
    return acc;
  }, {});
  for (const h of SENSITIVE_HEADER_NAMES) {
    if (lower[h]) {
      headers[lower[h]] = '[REDACTED]';
    }
  }
}

// ---------------------------------------------------------------------------
// Container keys – the ENTIRE value (including all children) is replaced
// with '[REDACTED]' so abbreviated / obfuscated child keys can't leak.
// ---------------------------------------------------------------------------
const PII_CONTAINERS = [
  'CCDetails', 'ccdetails', 'cc_details',
  'BillingAddress', 'billingAddress', 'billing_address',
  'BillingInfo', 'billinginfo',
  'ShippingAddress', 'shippingAddress', 'shipping_address',
  'ShippingInfo', 'shippinginfo',
  'paymentInstruments', 'paymentinstruments',
  'creditCard', 'credit_card',
  'paymentInstrument', 'payment_instrument',
  'customer', 'profile',
  'orderdetails', // order history / detail payloads (nested addresses, contact, payment)
  // site-config: corporate / default locations (addresses, phones)
  'defaultLocation', 'allLocations', 'DefaultLocation', 'AllLocations',
];
const piiContainerSet = new Set(PII_CONTAINERS.map(f => f.toLowerCase()));

/** URLs whose request/response bodies are fully redacted (SSO tokens, extauth, cart PII). */
function shouldRedactEntireNetworkBody(url) {
  if (!url || typeof url !== 'string') return false;
  const u = url.toLowerCase();
  return (
    u.includes('extauth')
    || u.includes('slas/intermediate')
    || u.includes('refresh-token')
    || u.includes('get-user-login')
    || (u.includes('me') && u.includes('dynamic'))
    // Cart / checkout: ship-to name, address, phone in API payloads (PII scan)
    || u.includes('calculate-shipping')
    || u.includes('get-cart-detail')
    || u.includes('place-order')
    || u.includes('confirm-order')
    // Order detail page: addresses, email, phone, card last4/exp in API payloads
    || u.includes('get-order-detail')
    || u.includes('get-customer-address')
  );
}

/** Query parameter names to replace with [REDACTED] in captured URLs. Intentionally empty so e.g. LoginSessionID is visible in LogRocket for SSO debugging. */
const SENSITIVE_URL_PARAM_NAMES = new Set([]);

// ---------------------------------------------------------------------------
// Input names to mask in the DOM – uses **exact match** (not substring) so
// generic selects (color, size, embroidery) on PDP are left untouched.
// ---------------------------------------------------------------------------
const PII_INPUT_NAMES = new Set([
  // Names
  'name', 'firstname', 'first_name', 'lastname', 'last_name',
  'fullname', 'full_name', 'displayname', 'display_name',
  'username', 'user_name',
  // Contact
  'email', 'emailaddress', 'email_address',
  'phone', 'phoneno', 'phone_no', 'phonenumber', 'phone_number',
  'mobile', 'mobilenumber', 'mobile_number', 'fax',
  // Address
  'address', 'address1', 'address2',
  'addressline1', 'addressline2', 'address_line_1', 'address_line_2',
  'street', 'city', 'state', 'statecode', 'state_code',
  'zip', 'zipcode', 'zip_code', 'postalcode', 'postal_code',
  'country', 'countrycode', 'country_code',
  // Payment / financial (react-credit-cards uses name="number" for PAN)
  'number',
  'cardnumber', 'card_number', 'ccnumber', 'cc_number',
  'cvv', 'cvc', 'securitycode', 'security_code',
  'expirationdate', 'expiration_date',
  'expmonth', 'expyear', 'exp_month', 'exp_year',
  'accountnumber', 'account_number',
  'routingnumber', 'routing_number',
  'iban', 'bic', 'pan', 'cardholdername', 'cardholder_name',
  // Auth
  'password',
  'ssn', 'socialsecuritynumber', 'social_security_number',
  'dob', 'dateofbirth', 'date_of_birth', 'birthdate', 'birth_date',
  // App-specific
  'employeeid', 'employee_id',
]);

/**
 * Recursively redact PII values from a plain object / array.
 * Returns a new object – the original is never mutated.
 */
function redactPII(obj) {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(redactPII);
  if (typeof obj !== 'object') return obj;

  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    const lk = key.toLowerCase();
    if (piiContainerSet.has(lk)) {
      cleaned[key] = '[REDACTED]';
    } else if (isSensitiveKey(lk)) {
      cleaned[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      cleaned[key] = redactPII(value);
    } else {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

/**
 * Sanitize a request/response body string.
 * Attempts JSON parse → redact → re-stringify; non-JSON bodies are redacted entirely.
 */
function sanitizeBody(body, url) {
  if (!body) return body;
  if (shouldRedactEntireNetworkBody(url)) {
    return '[REDACTED]';
  }
  try {
    const parsed = JSON.parse(body);
    return JSON.stringify(redactPII(parsed));
  } catch {
    return '[REDACTED]';
  }
}

/**
 * Sanitize URL query-string parameters that may contain PII.
 */
function sanitizeUrl(url) {
  if (!url) return url;
  try {
    const u = new URL(url, window.location.origin);
    for (const key of u.searchParams.keys()) {
      const lk = key.toLowerCase();
      if (
        piiSet.has(lk)
        || lk.includes('password')
        || SENSITIVE_URL_PARAM_NAMES.has(lk)
      ) {
        u.searchParams.set(key, '[REDACTED]');
      }
    }
    return u.toString();
  } catch {
    return url;
  }
}

// ---------------------------------------------------------------------------
// Redux state keys whose values should be fully redacted in session data.
// ---------------------------------------------------------------------------
const REDACTED_STATE_KEYS = [
  'userReducer',
  'checkout',
  'cartReducer',
  'orderReducer',
  'adminApi', // RTK Query cache (order detail, addresses, profile, etc.)
];

/**
 * Sanitise a Redux state snapshot so no PII leaks into LogRocket.
 */
function sanitizeReduxState(state) {
  if (!state || typeof state !== 'object') return state;
  const sanitized = { ...state };
  for (const key of REDACTED_STATE_KEYS) {
    if (key in sanitized) {
      sanitized[key] = '[REDACTED]';
    }
  }
  return sanitized;
}

function actionSanitizer(action) {
  const type = action.type || '';
  if (
    type.startsWith('user/')
    || type.startsWith('checkout/')
    || type.startsWith('order/')
    || type.startsWith('adminApi/')
  ) {
    return { ...action, payload: '[REDACTED]' };
  }
  return action;
}

// ---------------------------------------------------------------------------
// Initialise LogRocket (call after site config; idempotent)
// ---------------------------------------------------------------------------
let logRocketInitialized = false;
let logRocketInnerMiddleware = null;

/**
 * Resolve LogRocket app id: REACT_APP_LOGROCKET_APP_ID overrides SFCC when set.
 * Otherwise requires SFCC enableLogRocket + non-empty logRocketAppID.
 */
export function getResolvedLogRocketAppId(site) {
  const env = process.env.REACT_APP_LOGROCKET_APP_ID;
  if (env != null && String(env).trim() !== '') {
    return String(env).trim();
  }
  if (!site?.enableLogRocket) {
    return '';
  }
  const fromSite = site?.logRocketAppID;
  if (fromSite != null && String(fromSite).trim() !== '') {
    return String(fromSite).trim();
  }
  return '';
}

export function isLogRocketInitialized() {
  return logRocketInitialized;
}

export function initLogRocket(resolvedAppId) {
  if (!resolvedAppId || logRocketInitialized) {
    return;
  }

  LogRocket.init(resolvedAppId, {
    shouldCaptureIP: false,

    dom: {
      inputSanitizer: (inputEl) => {
        const type = (inputEl.type || '').toLowerCase();
        if (type === 'password' || type === 'hidden') return '*';

        const id = (inputEl.id || inputEl.name || '').toLowerCase();
        if (PII_INPUT_NAMES.has(id)) return '*';
      },
      textSanitizer: false,
    },

    network: {
      requestSanitizer(request) {
        const reqUrl = request.url || '';
        request.url = sanitizeUrl(reqUrl);
        sanitizeHeaders(request.headers);
        request.body = sanitizeBody(request.body, reqUrl);
        return request;
      },

      responseSanitizer(response) {
        sanitizeHeaders(response.headers);
        const resUrl = response.url || response.request?.url || '';
        response.body = sanitizeBody(response.body, resUrl);
        return response;
      },
    },

    console: {
      isEnabled: process.env.NODE_ENV !== 'production',
    },
  });

  logRocketInnerMiddleware = LogRocket.reduxMiddleware({
    stateSanitizer: sanitizeReduxState,
    actionSanitizer,
  });

  setupLogRocketReact(LogRocket);
  logRocketInitialized = true;
}

// ---------------------------------------------------------------------------
// Redux middleware creator (call inside configureStore)
// ---------------------------------------------------------------------------
export function getLogRocketReduxMiddleware() {
  return [
    (store) => (next) => (action) => {
      if (!logRocketInnerMiddleware) {
        return next(action);
      }
      return logRocketInnerMiddleware(store)(next)(action);
    },
  ];
}

// ---------------------------------------------------------------------------
// Identify a user after authentication (deterministic SHA-256 of BECustNo + optional IP hash trait)
// ---------------------------------------------------------------------------
async function sha256Hex(input) {
  const buf = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(input),
  );
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function identifyUser(userData, loginSessionID = null, ipHashFromRedux = null) {
  if (!logRocketInitialized) return;
  const raw = userData?.BECustNo;
  if (raw == null || String(raw).trim() === '') return;

  const beCustNoHash = await sha256Hex(String(raw).trim());
  const traits = {};
  
  // Use IP hash from Redux (site-config) if available, fallback to userData
  const ipHash = ipHashFromRedux || userData.LrClientIpHash;
  if (ipHash) {
    traits.ipHash = ipHash;
  }
  
  // Add LoginSessionID for SSO logins
  if (loginSessionID) {
    traits.loginSessionID = loginSessionID;
  }
  
  LogRocket.identify(beCustNoHash, traits);
}

export default LogRocket;
